import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import { Conversation } from '../../conversation.js';
import { Exam, type ExamState } from '../../exam.js';
import { ALL_SUBJECTS, normalizeSubject, type Subject } from '../../subjects.js';
import { syncTeacherClassFromExam } from '../../utils/teacher-classes.js';
import { SET_EXAM_METADATA_TASK_CLASS } from '../pending-tasks/set-exam-metadata.task.js';

const DEFAULT_LNG = 'de';

/** Resolve an i18n code from a stored conversation language, which may be a code ("de") or a label ("Deutsch"). */
function resolveLngCode(language?: string | null): string {
  const v = String(language ?? '').trim().toLowerCase();
  if (!v) return DEFAULT_LNG;
  const byValue = SUPPORTED_LANGUAGES.find(l => l.value === v);
  if (byValue) return byValue.value;
  const byLabel = SUPPORTED_LANGUAGES.find(l => l.label.toLowerCase() === v);
  return byLabel ? byLabel.value : DEFAULT_LNG;
}

/** Localized display label for a subject key (used to build a human-readable exam title). */
function localizedSubject(subjectKey: Subject, lng: string): string {
  return String(i18next.t(`Subjects.${subjectKey}`, { lng, defaultValue: subjectKey }));
}

export const metadataToolDef = [
  {
    type: 'function',
    function: {
      name: 'set_metadata',
      description:
        'Setzt die Klausurmetadaten (Fach, Thema, optional Klasse). Zwei Aufrufsituationen:\n' +
        '1) PENDING TASK "SetExamMetadataPendingTask" im Kontext (Werte automatisch aus einer hochgeladenen Klausur extrahiert) — rufe dieses Tool sofort damit auf.\n' +
        'Wenn die Klasse gefunden wurde, alle drei Parameter übergeben. Wenn nicht, nur fach und thema — dann die Klasse anschließend beim Nutzer erfragen, diese wird für die weiteren Schritte aber nicht zwingend benötigt.\n' +
        '2) NEUE KLAUSUR WIRD MIT create_exam ERSTELLT: rufe dieses Tool mit Fach/Thema/Klasse auf direkt vor dem create_exam-Aufruf nachdem der Plan bestätigt wurde, nicht danach.',
      parameters: {
        type: 'object',
        properties: {
          fach: {
            type: 'string',
            enum: [...ALL_SUBJECTS],
            description:
              'Standardisierter Fach-Key der Klausur — exakt einer der erlaubten Enum-Werte (z.B. "math", "physics"). Nutze den Key aus dem Pending Task; falls keiner passt, "other".',
          },
          thema: { type: 'string', description: 'Thema der Klausur' },
          klasse: { type: 'string', description: 'Klasse/Stufe (optional, nur wenn auf dem Blatt gefunden)' },
        },
        required: ['fach', 'thema'],
      },
    },
  },
];

export async function handleMetadata(exam: Exam, toolCallArgs: any): Promise<object> {
  const examId = exam._id;
  if (!examId) return { content: 'Fehler: Keine Klausur-ID vorhanden.' };

  // Coerce to a canonical subject key so only enum values reach storage; `fachLabel` is the
  // localized display form used in the human-readable title and user-facing messages.
  const fach = normalizeSubject(toolCallArgs?.fach);
  const thema = String(toolCallArgs?.thema ?? '').trim();
  const klasse = typeof toolCallArgs?.klasse === 'string' ? toolCallArgs.klasse.trim() : undefined;
  const hasClass = Boolean(klasse && klasse.length > 0);

  if (!thema) {
    return { content: 'Fehler: Fach und Thema müssen angegeben werden.' };
  }

  try {
    const examCollection = await getCollection(Exam as any);

    // Localize the subject for the title using the conversation language (falls back to German).
    let lng = DEFAULT_LNG;
    try {
      if (exam.conversationId) {
        const conversation = await (await getCollection(Conversation)).findOne({ _id: exam.conversationId });
        lng = resolveLngCode((conversation as any)?.language);
      }
    } catch (langErr) {
      logger.warn({ err: langErr, examId, context: 'SetMetadata' }, 'Failed to resolve conversation language');
    }
    const fachLabel = localizedSubject(fach, lng);

    const updateFields: Record<string, unknown> = {
      subject: fach,
      topic: thema,
    };

    if (hasClass) {
      updateFields['class'] = klasse;
      updateFields['title'] = `${klasse} - ${fachLabel} - ${thema}`;
      updateFields['examState.metaDataSet'] = true;
    } else {
      updateFields['title'] = `${fachLabel} - ${thema}`;
    }

    await examCollection.updateOne({ _id: examId } as any, { $set: updateFields });

    if (hasClass) {
      await syncTeacherClassFromExam(examId, klasse);
    }

    // Remove the pending task — it has been processed
    await examCollection.updateOne({ _id: examId } as any, {
      $pull: { 'examState.pendingTasks': { _class: SET_EXAM_METADATA_TASK_CLASS } } as any,
    });

    logger.info(
      { examId, fach, fachLabel, thema, klasse: klasse ?? 'N/A', metaDataSet: hasClass, context: 'SetMetadata' },
      'Exam metadata set',
    );

    if (hasClass) {
      return {
        content: `Metadaten gesetzt: Fach="${fachLabel}", Thema="${thema}", Klasse="${klasse}". Teiel diese Informationendem Nutzer mit und, dass sie in der Seitenleiste (Klausur-Details) jederzeit bearbeitet werden können.`,
      };
    }

    return {
      content: `Fach ("${fachLabel}") und Thema ("${thema}") wurden für die Generierung von Musterlösung und Bewertungsschema gespeichert. Die Klasse wurde nicht auf dem Klausurblatt gefunden — bitte beim Nutzer nachfragen.`,
    };
  } catch (err) {
    logger.error({ err, examId, context: 'SetMetadata' }, 'Failed for exam');
    return { content: 'Fehler beim Setzen der Metadaten.' };
  }
}

export function metadataAvailability(examState: ExamState): boolean {
  return !examState.metaDataSet;
}
